import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootState } from '../../../store/store';
import {
  useGetDocumentRequirementHistory,
  useListDocumentRequirements,
  useUpdateDocumentRequirement,
} from '../../../services/loanService';
import type { DocumentRequirement } from '../../../services/schemas/loanServiceSchemas/documentRequirement';
import { getFundingProductName } from '../../../constants/fundingProducts';
import { formatSouthAfricanDateTime } from '../../../utils/southAfricanTime';

/** The stages a loan passes through, in order; a document blocks its stage and every later one. */
export const REQUIREMENT_STAGES: { value: string; label: string }[] = [
  { value: 'ASSESSMENT', label: 'Assessment' },
  { value: 'FINAL_APPROVAL', label: 'Final approval' },
  { value: 'CONTRACTING', label: 'Contracting' },
  { value: 'PAYOUT', label: 'Payout' },
  { value: 'POST_DISBURSEMENT', label: 'Post-disbursement' },
];

export const stageLabel = (stage?: string) => REQUIREMENT_STAGES.find(s => s.value === stage)?.label || stage || '-';

export const requirementProductName = (product?: string) => {
  if (product === 'ALL') return 'All products';
  if (product === 'SECURED') return 'Secured / asset-backed';
  return getFundingProductName(product);
};

const FIELD_LABELS: Record<string, string> = {
  applies: 'Applies',
  transactionSpecific: 'Transaction specific',
  compulsory: 'Compulsory',
  blockingStage: 'Blocking stage',
  totalExpected: 'Total expected',
  minimumAtGate: 'Minimum at gate',
  fullCompletionStage: 'Full completion stage',
  reuseIfCurrent: 'Reuse if current',
};

type Editable = Pick<DocumentRequirement, 'applies' | 'transactionSpecific' | 'compulsory' | 'blockingStage' | 'totalExpected'
  | 'minimumAtGate' | 'fullCompletionStage' | 'reuseIfCurrent'>;

const editable = (r: DocumentRequirement): Editable => ({
  applies: !!r.applies,
  transactionSpecific: !!r.transactionSpecific,
  compulsory: !!r.compulsory,
  blockingStage: r.blockingStage,
  totalExpected: r.totalExpected ?? 0,
  minimumAtGate: r.minimumAtGate ?? 0,
  fullCompletionStage: r.fullCompletionStage,
  reuseIfCurrent: !!r.reuseIfCurrent,
});

const stageIndex = (stage?: string) => REQUIREMENT_STAGES.findIndex(s => s.value === stage);

/** Why a row's values would not save, or undefined when they are fine. */
const problem = (e: Editable) => {
  if ((e.minimumAtGate ?? 0) > (e.totalExpected ?? 0)) return 'The minimum cannot be more than the total expected';
  if ((e.totalExpected ?? 0) < 0 || (e.minimumAtGate ?? 0) < 0) return 'Quantities cannot be negative';
  return undefined;
};

function HistoryModal({ rule, onClose }: { rule: DocumentRequirement; onClose: () => void }) {
  const { data, isLoading } = useGetDocumentRequirementHistory(rule.ruleId || '', { query: { enabled: !!rule.ruleId } });
  const changes = data || [];
  const show = (field?: string, value?: string) => {
    if (value === 'true') return 'Yes';
    if (value === 'false') return 'No';
    if (field === 'blockingStage' || field === 'fullCompletionStage') return stageLabel(value);
    return value ?? '-';
  };
  return (
    <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.4)' }} role="dialog" aria-modal="true">
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title mb-0">Change history · {rule.ruleId}</h5>
              <small className="text-muted">{rule.documentName} · {requirementProductName(rule.fundingProduct)}</small>
            </div>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {isLoading && <div className="text-center py-3"><div className="spinner-border spinner-border-sm text-success" role="status"></div></div>}
            {!isLoading && changes.length === 0 && <p className="text-muted mb-0">No changes since this rule was loaded from the implementation guide.</p>}
            {changes.length > 0 && (
              <table className="table table-sm align-middle mb-0">
                <thead className="small text-muted"><tr><th>When</th><th>Who</th><th>Field</th><th>From</th><th>To</th></tr></thead>
                <tbody>
                  {changes.map((c, i) => (
                    <tr key={`${c.changedAt}-${c.field}-${i}`}>
                      <td className="small text-nowrap">{c.changedAt ? formatSouthAfricanDateTime(c.changedAt) : '-'}</td>
                      <td className="small">{c.changedBy || '-'}</td>
                      <td className="small">{FIELD_LABELS[c.field || ''] || c.field}</td>
                      <td className="small text-muted">{show(c.field, c.oldValue)}</td>
                      <td className="small fw-semibold">{show(c.field, c.newValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StageSelect({ value, onChange, label }: { value?: string; onChange: (v: string) => void; label: string }) {
  return (
    <select className="form-select form-select-sm" style={{ minWidth: 130 }} value={value || ''} aria-label={label}
      onChange={e => onChange(e.target.value)}>
      {REQUIREMENT_STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
    </select>
  );
}

function Toggle({ checked, onChange, label }: { checked?: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <div className="form-check form-switch d-flex justify-content-center mb-0">
      <input className="form-check-input" type="checkbox" role="switch" checked={!!checked} aria-label={label}
        onChange={e => onChange(e.target.checked)} />
    </div>
  );
}

function RequirementRow({ rule, changedBy, onSaved, onHistory }: {
  rule: DocumentRequirement;
  changedBy?: string;
  onSaved: () => void;
  onHistory: () => void;
}) {
  const [draft, setDraft] = useState<Editable>(editable(rule));
  useEffect(() => { setDraft(editable(rule)); }, [rule]);
  const update = useUpdateDocumentRequirement();
  const dirty = JSON.stringify(draft) !== JSON.stringify(editable(rule));
  const invalid = problem(draft);
  const set = <K extends keyof Editable>(key: K, value: Editable[K]) => setDraft(d => ({ ...d, [key]: value }));
  const fullBeforeBlocking = stageIndex(draft.fullCompletionStage) < stageIndex(draft.blockingStage);

  const save = () => {
    update.mutate(
      { ruleId: rule.ruleId || '', data: { ...draft, changedBy } as any },
      {
        onSuccess: () => { toast.success(`${rule.ruleId} saved`); onSaved(); },
        onError: (e: any) => toast.error(e?.response?.data?.message || e?.message || `${rule.ruleId} could not be saved`),
      },
    );
  };

  return (
    <tr className={draft.applies ? '' : 'text-muted'} style={draft.applies ? undefined : { opacity: 0.6 }}>
      <td className="ps-3 small text-nowrap">{rule.ruleId}</td>
      <td style={{ minWidth: 220 }}>
        <div className="fw-semibold small">{rule.documentName}{rule.perDirector && <span className="badge bg-light text-dark border ms-2">Each director</span>}</div>
        {rule.validityNote && <div className="small text-muted">{rule.validityNote}</div>}
        {rule.notes && <div className="small text-muted fst-italic">{rule.notes}</div>}
      </td>
      <td><Toggle checked={draft.applies} onChange={v => set('applies', v)} label={`${rule.ruleId} applies`} /></td>
      <td><Toggle checked={draft.transactionSpecific} onChange={v => set('transactionSpecific', v)} label={`${rule.ruleId} transaction specific`} /></td>
      <td><Toggle checked={draft.compulsory} onChange={v => set('compulsory', v)} label={`${rule.ruleId} compulsory`} /></td>
      <td><StageSelect value={draft.blockingStage} onChange={v => set('blockingStage', v)} label={`${rule.ruleId} blocking stage`} /></td>
      <td>
        <input type="number" min={0} className="form-control form-control-sm" style={{ width: 70 }} value={draft.totalExpected ?? 0}
          aria-label={`${rule.ruleId} total expected`} onChange={e => set('totalExpected', Number(e.target.value))} />
      </td>
      <td>
        <input type="number" min={0} className={`form-control form-control-sm ${invalid ? 'is-invalid' : ''}`} style={{ width: 70 }}
          value={draft.minimumAtGate ?? 0} aria-label={`${rule.ruleId} minimum at gate`} onChange={e => set('minimumAtGate', Number(e.target.value))} />
      </td>
      <td>
        <StageSelect value={draft.fullCompletionStage} onChange={v => set('fullCompletionStage', v)} label={`${rule.ruleId} full completion stage`} />
        {fullBeforeBlocking && (
          <div className="small text-warning-emphasis mt-1" title="The full quantity is due before the stage this document blocks, so it will hold the earlier stage.">
            <i className="fa fa-exclamation-triangle me-1"></i>Earlier than blocking stage
          </div>
        )}
      </td>
      <td><Toggle checked={draft.reuseIfCurrent} onChange={v => set('reuseIfCurrent', v)} label={`${rule.ruleId} reuse if current`} /></td>
      <td className="text-end pe-3 text-nowrap">
        {invalid && <div className="small text-danger mb-1">{invalid}</div>}
        <button className="btn btn-sm btn-primary me-1" disabled={!dirty || !!invalid || update.isPending} onClick={save}>
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
        {dirty && <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => setDraft(editable(rule))}>Undo</button>}
        <button className="btn btn-sm btn-outline-secondary" title="Change history" aria-label={`${rule.ruleId} history`} onClick={onHistory}>
          <i className="fa fa-history"></i>
        </button>
        {rule.updatedBy && (
          <div className="small text-muted mt-1">Changed by {rule.updatedBy}{rule.updatedAt ? ` · ${formatSouthAfricanDateTime(rule.updatedAt)}` : ''}</div>
        )}
      </td>
    </tr>
  );
}

/**
 * Configure Requirements: the document matrix from the implementation guide, editable here so the business can
 * change which documents a product needs, the stage each blocks and the quantities, without a code change.
 */
const DocumentRequirementsSettings: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const changedBy = user?.name || user?.username || undefined;
  const { data, isLoading, isError, refetch } = useListDocumentRequirements();
  const [product, setProduct] = useState<string>('ALL');
  const [search, setSearch] = useState('');
  const [history, setHistory] = useState<DocumentRequirement>();
  const rules = useMemo(() => data || [], [data]);

  const products = useMemo(() => {
    const seen: string[] = [];
    rules.forEach(r => { if (r.fundingProduct && !seen.includes(r.fundingProduct)) seen.push(r.fundingProduct); });
    return seen;
  }, [rules]);

  const shown = rules.filter(r => r.fundingProduct === product)
    .filter(r => !search.trim() || `${r.ruleId} ${r.documentName} ${r.documentGroup}`.toLowerCase().includes(search.trim().toLowerCase()));
  const groups = shown.reduce<Record<string, DocumentRequirement[]>>((acc, r) => {
    const key = r.documentGroup || 'Other';
    (acc[key] = acc[key] || []).push(r);
    return acc;
  }, {});

  return (
    <div>
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-4 bg-primary-gradient">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item"><Link className="text-white" to="/">
                  <i className="fa fa-home me-2" aria-hidden="true"></i>Home
                </Link></li>
                <li className="breadcrumb-item"><Link className="text-white" to="/settings">Settings</Link></li>
                <li className="breadcrumb-item active text-white" aria-current="page">Document Requirements</li>
              </ol>
            </div>
          </div>
          <div className="row align-items-center">
            <div className="col-md-8">
              <h1 className="fs-2 fw-bold mb-2 text-white">Configure Requirements</h1>
              <p className="mb-0 text-white fs-6 opacity-75">
                Which documents each funding product needs, the stage a missing one blocks, and how many release the gate.
                Every change is recorded with who made it.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          {isLoading && <div className="text-center py-5"><div className="spinner-border text-success" role="status"></div></div>}
          {isError && <div className="alert alert-danger">The document requirements could not be loaded. <button className="btn btn-link p-0" onClick={() => refetch()}>Try again</button></div>}
          {!isLoading && !isError && (
            <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
              <div className="card-body border-bottom">
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <ul className="nav nav-pills gap-1">
                    {products.map(p => (
                      <li className="nav-item" key={p}>
                        <button className={`nav-link py-1 px-3 ${product === p ? 'active' : ''}`} onClick={() => setProduct(p)}>
                          {requirementProductName(p)}
                          <span className={`badge ms-2 ${product === p ? 'bg-light text-dark' : 'bg-secondary-subtle text-secondary'}`}>
                            {rules.filter(r => r.fundingProduct === p).length}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <input className="form-control form-control-sm" style={{ maxWidth: 240 }} placeholder="Search documents"
                    value={search} onChange={e => setSearch(e.target.value)} aria-label="Search documents" />
                </div>
                <small className="text-muted d-block mt-2">
                  {product === 'ALL'
                    ? 'These apply to every business application, whatever the product.'
                    : product === 'SECURED'
                      ? 'These join an application when it is asset finance or has collateral captured.'
                      : `These join an application when its funding product is ${requirementProductName(product)}.`}
                  {' '}A compulsory document blocks its stage while fewer than the minimum are in, and the stage it is due in full until all are in.
                </small>
              </div>
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead className="small text-muted">
                    <tr>
                      <th className="ps-3">Rule</th><th>Document</th><th className="text-center">Applies</th>
                      <th className="text-center">Transaction specific</th><th className="text-center">Compulsory</th>
                      <th>Blocking stage</th><th>Total</th><th>Minimum</th><th>Full completion</th>
                      <th className="text-center">Reuse if current</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(groups).map(([group, rows]) => (
                      <React.Fragment key={group}>
                        <tr className="table-light"><td colSpan={11} className="ps-3 small fw-semibold text-uppercase text-muted">{group}</td></tr>
                        {rows.map(r => (
                          <RequirementRow key={r.ruleId} rule={r} changedBy={changedBy} onSaved={() => refetch()} onHistory={() => setHistory(r)} />
                        ))}
                      </React.Fragment>
                    ))}
                    {shown.length === 0 && <tr><td colSpan={11} className="text-center text-muted py-4">No documents match.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      {history && <HistoryModal rule={history} onClose={() => setHistory(undefined)} />}
    </div>
  );
};

export default DocumentRequirementsSettings;
